import { computed, onMounted, reactive, toRefs } from 'vue'

import { encouragementSticker, rewardSticker, specialSticker } from '@/data/sticker'
import useSpaceApi from '../api/useSpaceApi'
import moment from 'moment'
import {
  getStickerThumbUrl,
  getStickerUrl,
  sortSpecialStickerDate,
} from '../../helpers/sticker.helper'
import { ISpecialSticker, IStickerItem } from '../../types/interfaces/sticker.interface'
import useSocketEvent from './useSocketEvent'

interface StickerObjectInterFace {
  stickerPath: string
  fullscreen: boolean
}

interface UseStickerState {
  stickers: Record<string, IStickerItem[]>
  categories: string[]
  activeCategory: string
  showingSticker: boolean
  showingFullScreen: boolean
  stickerPathToShow?: string
}

const DISPLAY_DURATION = 2500
export default function useSticker() {
  const state = reactive<UseStickerState>({
    stickers: {},
    categories: ['encouragement', 'reward', 'special'],
    activeCategory: 'encouragement',
    showingSticker: false,
    showingFullScreen: false,
    stickerPathToShow: undefined,
  })
  const { socket } = useSocketEvent()
  const { saveStickers } = useSpaceApi()

  const fullscreen = computed(
    () => state.activeCategory === 'reward' || state.activeCategory === 'special',
  )

  const toStickerItems = (ids: string[]): IStickerItem[] =>
    ids.map((id) => ({ id, url: getStickerUrl(id), thumbUrl: getStickerThumbUrl(id) }))

  const initSticker = () => {
    const today = moment()
    state.stickers.encouragement = toStickerItems(encouragementSticker)
    state.stickers.reward = toStickerItems(rewardSticker)

    // Copy so the module-level list isn't mutated on every mount
    const [firstSpecialSticker, ...restSpecialSticker] = [...specialSticker]
    const specialStickerOrderByDate = restSpecialSticker.sort((i: ISpecialSticker) =>
      sortSpecialStickerDate({ date: today, stickerList: i }),
    )
    state.stickers.special = toStickerItems(
      [firstSpecialSticker, ...specialStickerOrderByDate].flatMap((i) => i.ids),
    )
  }

  let prevTimeoutId: NodeJS.Timeout

  socket.on('stickerPop', (stickerObject: StickerObjectInterFace) => {
    displaySticker(stickerObject)
  })
  const displaySticker = (stickerObject: StickerObjectInterFace) => {
    state.showingFullScreen = false
    // const { stickerPath, fullscreen } = stickerObject;
    if (stickerObject?.stickerPath) {
      if (prevTimeoutId) clearTimeout(prevTimeoutId)
      state.stickerPathToShow = stickerObject?.stickerPath
      state.showingSticker = true
      state.showingFullScreen = stickerObject?.fullscreen

      prevTimeoutId = setTimeout(() => {
        state.showingSticker = false
        state.showingFullScreen = false
      }, DISPLAY_DURATION)
    }
  }

  const sendSticker = async (stickerPath: string) => {
    const emitData = { stickerPath, fullscreen: fullscreen.value }
    socket?.emit('stickerPop', emitData)
    await saveStickers({
      stickerPath,
      category: state.activeCategory,
    })
  }

  const changeCategory = (category: string) => {
    state.activeCategory = category
  }

  onMounted(() => {
    initSticker()
  })

  return { ...toRefs(state), fullscreen, changeCategory, sendSticker }
}
